import { FieldParser, ValidationWarning } from './types';

/**
 * Passed where a parser's **default** goes, to say there isn't one: the value
 * is unreadable rather than substituted.
 *
 * Only a parser that HAS a default can be told not to use it, which is the
 * point — it keeps "this field has no substitute" at the one layer entitled to
 * say it. An identity answering `0` for a missing id is not partial knowledge,
 * it is a fabrication: it collides React keys across rows and binds row actions
 * to record 0 (#1178). Same for a page total, or a list arriving as a scalar.
 *
 * What it deliberately CANNOT say is anything about a container's contents —
 * `array(item, REJECT)` refuses a non-array, never a list that lost a row.
 * "How much loss can I live with" is the consumer's question, answered by which
 * `Received` arm it accepts, and an endpoint that answered it for every
 * consumer is the mechanism #1220 removed.
 *
 * The failure still carries the warning that explains it, so `problems` names
 * the path either way.
 */
export const REJECT = Symbol('REJECT');
export type Reject = typeof REJECT;

const writeDrift = (warnings: Map<string, ValidationWarning>, path: string, expected: string, received: unknown, usedDefault: unknown): void => {
  warnings.set(path, { path, expected, received, usedDefault });
};

/** As `writeDrift`, but marks the warning as a refusal — see `ValidationWarning.refused`. */
const writeRefusal = (warnings: Map<string, ValidationWarning>, path: string, expected: string, received: unknown): void => {
  warnings.set(path, { path, expected, received, usedDefault: undefined, refused: true });
};

export const str =
  (defaultValue: string | Reject = ''): FieldParser<string> =>
  (raw, path, warnings) => {
    if (typeof raw === 'string') return { ok: true, value: raw };
    if (defaultValue === REJECT) {
      writeRefusal(warnings, path, 'string', raw);
      return { ok: false };
    }
    writeDrift(warnings, path, 'string', raw, defaultValue);
    return { ok: true, value: defaultValue };
  };

export const num =
  (defaultValue: number | Reject = 0): FieldParser<number> =>
  (raw, path, warnings) => {
    if (typeof raw === 'number' && !Number.isNaN(raw)) {
      return { ok: true, value: raw };
    }
    if (defaultValue === REJECT) {
      writeRefusal(warnings, path, 'number', raw);
      return { ok: false };
    }
    writeDrift(warnings, path, 'number', raw, defaultValue);
    return { ok: true, value: defaultValue };
  };

export const bool =
  (defaultValue: boolean | Reject = false): FieldParser<boolean> =>
  (raw, path, warnings) => {
    if (typeof raw === 'boolean') return { ok: true, value: raw };
    if (defaultValue === REJECT) {
      writeRefusal(warnings, path, 'boolean', raw);
      return { ok: false };
    }
    writeDrift(warnings, path, 'boolean', raw, defaultValue);
    return { ok: true, value: defaultValue };
  };

/**
 * Accepts any value (including null and undefined) and returns it as
 * `unknown`. No defaulting, no warning — the wire shape is unknown by
 * design (e.g. opaque JSON payloads, loosely-typed config fields). The
 * caller is expected to narrow the result before using it.
 *
 * Wrap with `nullable` if a missing key should distinguish from an
 * intentional `null`.
 */
export const unknownValue = (): FieldParser<unknown> => (raw) => ({
  ok: true,
  value: raw,
});

/**
 * Like `unknownValue`, but guarantees the result is dereferenceable: `raw` must
 * be a plain object (non-null, non-array); its contents pass through verbatim as
 * `Record<string, unknown>` (opaque by design — dynamic JSON the caller
 * re-parses). A non-object (including `null`) fails (`ok: false`), so a consumer
 * that dereferences the value can't be handed a `null` that type-checks as an
 * object. Wrap with `withDefault(unknownRecord(), {})` for a scalar field, or use
 * `array(unknownRecord())` to drop malformed elements rather than pass them on.
 */
export const unknownRecord = (): FieldParser<Record<string, unknown>> => (raw, path, warnings) => {
  if (typeof raw === 'object' && raw !== null && !Array.isArray(raw)) {
    return { ok: true, value: raw as Record<string, unknown> };
  }
  writeDrift(warnings, path, 'object', raw, undefined);
  return { ok: false };
};

/**
 * Validates that `raw` is strictly equal (`===`) to one of `values`. Used
 * for string- and number-literal unions (enums). On mismatch, returns the
 * supplied `defaultValue` with a warning.
 *
 * Both `values` and `defaultValue` are linked to the same type parameter
 * `T`, which the `const` modifier infers as a literal tuple. As a result:
 *   - `oneOf(['a', 'b'], 'a')` returns `FieldParser<'a' | 'b'>` (narrowed).
 *   - `oneOf<['a', 'b']>([], 'a')` is a compile error (`[]` doesn't
 *     satisfy the tuple).
 *   - `oneOf(['a', 'b'], 'c')` is a compile error (`'c'` is not in T).
 */
export const oneOf =
  <const T extends readonly (string | number)[]>(values: T, defaultValue: T[number] | Reject): FieldParser<T[number]> =>
  (raw, path, warnings) => {
    if ((values as readonly unknown[]).includes(raw)) {
      return { ok: true, value: raw as T[number] };
    }
    const expected = `one of ${values.map((v) => JSON.stringify(v)).join(', ')}`;
    if (defaultValue === REJECT) {
      writeRefusal(warnings, path, expected, raw);
      return { ok: false };
    }
    writeDrift(warnings, path, expected, raw, defaultValue);
    return { ok: true, value: defaultValue };
  };
